import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { useToast } from '../hooks/use-toast';
import { HelpCircle, Send, ArrowLeft, Clock, CheckCircle, User, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SupportTicket = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    subject: '',
    category: '',
    priority: 'medium',
    description: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    if (user) {
      loadMyTickets();
    }
  }, [user]);

  const loadMyTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/my-tickets`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTicketReplies = async (ticketId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}/replies`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReplies(data.replies || []);
      }
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    loadTicketReplies(ticket.id);
  };

  const handleSendReply = async (ticketId) => {
    if (!replyMessage.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a message"
      });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets/${ticketId}/replies`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            message: replyMessage
          })
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Reply sent successfully"
        });
        setReplyMessage('');
        loadTicketReplies(ticketId);
        loadMyTickets();
      } else {
        throw new Error('Failed to send reply');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send reply"
      });
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { color: 'bg-blue-500', text: 'Open' },
      in_progress: { color: 'bg-yellow-500', text: 'In Progress' },
      resolved: { color: 'bg-green-500', text: 'Resolved' },
      closed: { color: 'bg-gray-500', text: 'Closed' }
    };
    const variant = variants[status] || variants.open;
    return <Badge className={`${variant.color} text-white`}>{variant.text}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'bg-gray-400',
      medium: 'bg-blue-400',
      high: 'bg-orange-400',
      urgent: 'bg-red-500',
      critical: 'bg-red-600'
    };
    return <Badge className={`${variants[priority] || variants.medium} text-white capitalize`}>{priority}</Badge>;
  };

  const categories = [
    { value: 'technical', label: 'Technical Support' },
    { value: 'account', label: 'Account & Access Issues' },
    { value: 'billing', label: 'Billing & Subscription' },
    { value: 'content', label: 'Cultural Content Issues' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low - General question' },
    { value: 'medium', label: 'Medium - Standard issue' },
    { value: 'high', label: 'High - Urgent problem' },
    { value: 'critical', label: 'Critical - Service disruption' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please log in to submit a support ticket."
      });
      return;
    }

    if (!formData.subject.trim() || !formData.category || !formData.description.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields."
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin-portal/support-tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          subject: formData.subject.trim(),
          category: formData.category,
          priority: formData.priority,
          description: formData.description.trim(),
          user_email: user?.email || 'unknown@example.com',
          user_name: user?.full_name || 'Unknown User'
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Ticket Submitted Successfully",
          description: `Your support ticket has been created. We'll get back to you soon!`
        });
        
        // Reset form
        setFormData({
          subject: '',
          category: '',
          priority: 'medium',
          description: ''
        });

        // Reload tickets
        loadMyTickets();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit ticket');
      }
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: error.message || "Failed to submit support ticket. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              Please log in to submit a support ticket.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/')} className="bg-green-700 hover:bg-green-800">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="text-center">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Support Ticket</h1>
            <p className="text-gray-600">
              Need help? Submit a support ticket and our team will get back to you as soon as possible.
            </p>
          </div>
        </div>

        {/* Support Ticket Form */}
        <Card>
          <CardHeader>
            <CardTitle>Create Support Ticket</CardTitle>
            <CardDescription>
              Please provide as much detail as possible to help us assist you better.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Information Display */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Your Information</h3>
                <p className="text-sm text-gray-600">Name: {user?.full_name || 'Not provided'}</p>
                <p className="text-sm text-gray-600">Email: {user?.email || 'Not provided'}</p>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  type="text"
                  placeholder="Brief description of your issue"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  required
                  maxLength={200}
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(priority => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce the problem, or relevant information that might help us assist you."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  required
                  rows={6}
                  maxLength={2000}
                />
                <p className="text-sm text-gray-500">
                  {formData.description.length}/2000 characters
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isSubmitting ? (
                    <>Submitting...</>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Open Tickets Section */}
        {selectedTicket ? (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  Ticket: {selectedTicket.subject}
                </CardTitle>
                <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Tickets
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                {getStatusBadge(selectedTicket.status)}
                {getPriorityBadge(selectedTicket.priority)}
                <Badge variant="outline">{selectedTicket.category}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Original Message */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="flex items-center mb-2">
                    <User className="w-4 h-4 mr-2 text-gray-600" />
                    <span className="font-medium text-sm">{selectedTicket.user_name}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {new Date(selectedTicket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700">{selectedTicket.message}</p>
                </div>

                <Separator />

                {/* Replies */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Conversation</h3>
                  {replies.length === 0 ? (
                    <p className="text-gray-500 text-sm">No replies yet</p>
                  ) : (
                    replies.map((reply) => (
                      <div 
                        key={reply.id} 
                        className={`p-4 rounded-lg border ${
                          reply.is_admin 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <User className="w-4 h-4 mr-2" />
                          <span className="font-medium text-sm">
                            {reply.sender_name}
                            {reply.is_admin && (
                              <Badge className="ml-2 bg-amber-600 text-white text-xs">Admin</Badge>
                            )}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {new Date(reply.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm">{reply.message}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Reply Form */}
                {selectedTicket.status !== 'closed' && (
                  <div className="space-y-2">
                    <Label>Your Reply</Label>
                    <Textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your response here..."
                      rows={4}
                    />
                    <Button onClick={() => handleSendReply(selectedTicket.id)}>
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-amber-600" />
                Your Open Support Tickets ({tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center text-gray-500">Loading tickets...</p>
              ) : tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">No open tickets. You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.filter(t => t.status === 'open' || t.status === 'in_progress').map((ticket) => (
                    <div
                      key={ticket.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleViewTicket(ticket)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-lg">{ticket.subject}</h3>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(ticket.status)}
                          {getPriorityBadge(ticket.priority)}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{ticket.message}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                        <span>Category: {ticket.category}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Before You Submit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600">
              <p><strong>Response Times:</strong></p>
              <ul className="ml-4 space-y-1">
                <li>• Critical: Within 2 hours</li>
                <li>• High: Within 4 hours</li>
                <li>• Medium: Within 1 business day</li>
                <li>• Low: Within 2 business days</li>
              </ul>
              <p><strong>Tips for Better Support:</strong></p>
              <ul className="ml-4 space-y-1">
                <li>• Be specific about the problem you're experiencing</li>
                <li>• Include any error messages you see</li>
                <li>• Mention what you were trying to do when the issue occurred</li>
                <li>• Include screenshots if relevant</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupportTicket;